import { Star, Quote, BadgeCheck } from "lucide-react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import AutoScroll from "embla-carousel-auto-scroll";

const testimonials = [
  {
    name: "Michael T.",
    initials: "MT",
    company: "Kitchen & Bathroom Renovations",
    location: "Sydney, NSW",
    rating: 5,
    timeAgo: "2 weeks ago",
    review: "Before FullScale, it was all referrals — some weeks busy, some weeks dead. Now we're getting 20+ qualified leads every month, consistently. We're ranking in suburbs we could never crack before, and our Google reviews tripled. Best marketing decision we've made.",
    avatarColor: "bg-blue-500"
  },
  {
    name: "Andrew K.",
    initials: "AK",
    company: "Electrical Services",
    location: "Brisbane, QLD",
    rating: 5,
    timeAgo: "1 month ago",
    review: "The AI response + missed call text-back has been a game changer for our electrical business. We used to lose jobs because we were on the tools. Now every enquiry gets an instant reply. Lead quality from Google Ads is up, tyre-kickers are down.",
    avatarColor: "bg-purple-500"
  },
  {
    name: "David R.",
    initials: "DR",
    company: "Roofing & Repairs",
    location: "Melbourne, VIC",
    rating: 5,
    timeAgo: "3 weeks ago",
    review: "We went from barely getting Google reviews to 5× more reviews in under two months. Customers trust us before we even arrive, and our quote acceptance rate has jumped. Finally look as professional online as our work is on site.",
    avatarColor: "bg-emerald-500"
  },
  {
    name: "James P.",
    initials: "JP",
    company: "Remedial Construction",
    location: "Newcastle, NSW",
    rating: 5,
    timeAgo: "1 week ago",
    review: "Our tender and enquiry volume skyrocketed after the new website. The AI chatbot qualifies people properly and saves us hours every week. FullScale actually understands the construction industry — results speak for themselves.",
    avatarColor: "bg-orange-500"
  },
  {
    name: "Tom M.",
    initials: "TM",
    company: "Plumbing Solutions",
    location: "Perth, WA",
    rating: 5,
    timeAgo: "5 days ago",
    review: "We were invisible on Google before FullScale. Now we're ranking top 3 for our key suburbs in Perth and the phone doesn't stop. These are quality leads ready to book, not price shoppers. Best plumber marketing we've found.",
    avatarColor: "bg-pink-500"
  },
  {
    name: "Chris B.",
    initials: "CB",
    company: "HVAC & Air Conditioning",
    location: "Adelaide, SA",
    rating: 5,
    timeAgo: "2 weeks ago",
    review: "I was skeptical about digital marketing, but results speak for themselves. Our website looks incredible, we're getting 15+ enquiries a week from Google Ads, and our Google Business Profile is finally working. Should have done this years ago.",
    avatarColor: "bg-cyan-500"
  },
  {
    name: "Mark L.",
    initials: "ML",
    company: "Landscape & Garden Design",
    location: "Gold Coast, QLD",
    rating: 5,
    timeAgo: "3 weeks ago",
    review: "The automated follow-up system alone has paid for itself ten times over. We used to lose half our landscaping leads to slow responses. Now we capture every single one, and our close rate has nearly doubled.",
    avatarColor: "bg-lime-600"
  },
  {
    name: "Tom H.",
    initials: "TH",
    company: "Commercial Painting",
    location: "Canberra, ACT",
    rating: 5,
    timeAgo: "1 month ago",
    review: "FullScale rebuilt our entire online presence in weeks. New website, optimised Google Business Profile, review system — the works. We've had our best quarter ever competing with the big painting contractors.",
    avatarColor: "bg-indigo-500"
  },
  {
    name: "Steve W.",
    initials: "SW",
    company: "Fencing & Decking",
    location: "Melbourne, VIC",
    rating: 5,
    timeAgo: "4 days ago",
    review: "The transparency is what sold me on FullScale. I can see exactly where every lead comes from and what's working. No more guessing games with marketing. They actually deliver on what they promise.",
    avatarColor: "bg-rose-500"
  }
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" role="img" aria-label={`${rating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < rating ? "fill-amber-400 text-amber-400" : "fill-muted text-muted"
          }`}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial }: { testimonial: typeof testimonials[0] }) {
  return (
    <article className="group relative bg-card rounded-xl p-5 sm:p-6 border border-border/50 shadow-card hover:shadow-lg transition-all duration-300 h-full">
      {/* Quote icon */}
      <Quote className="absolute top-4 right-4 w-7 h-7 text-primary/10" aria-hidden="true" />

      {/* Header with profile */}
      <div className="flex items-start gap-3 mb-3">
        {/* Avatar */}
        <div
          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${testimonial.avatarColor} flex items-center justify-center flex-shrink-0`}
          aria-hidden="true"
        >
          <span className="text-white font-bold text-[0.875rem] sm:text-[0.9375rem]">
            {testimonial.initials}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <p className="font-bold text-foreground text-[0.9375rem] sm:text-base">
              {testimonial.name}
            </p>
            <BadgeCheck className="w-3.5 h-3.5 text-blue-500" aria-label="Verified review" />
          </div>
          <p className="text-[0.8125rem] sm:text-[0.875rem] text-muted-foreground">
            {testimonial.company} • {testimonial.location}
          </p>
        </div>
      </div>

      {/* Rating & Time */}
      <div className="flex items-center gap-2 mb-3">
        <StarRating rating={testimonial.rating} />
        <span className="text-xs sm:text-[0.8125rem] text-muted-foreground">
          {testimonial.timeAgo}
        </span>
      </div>

      {/* Review Text */}
      <blockquote className="text-foreground/90 leading-[1.7] text-[0.9375rem] sm:text-base">
        "{testimonial.review}"
      </blockquote>
    </article>
  );
}

export function ResultsSection() {
  const [emblaRef] = useEmblaCarousel(
    { 
      loop: true, 
      align: "start",
      dragFree: true,
    },
    [AutoScroll({ playOnInit: true, speed: 0.8, stopOnInteraction: false, stopOnMouseEnter: true })]
  );

  return (
    <section id="results-section" className="section-padding bg-background relative overflow-hidden" aria-labelledby="results-heading">
      {/* Background accents */}
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-primary/[0.03] rounded-full blur-[80px] sm:blur-[100px] translate-x-1/2 -translate-y-1/2" />
      <div className="absolute inset-0 grid-pattern opacity-25" />

      <div className="container-wide relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center mb-6 sm:mb-8"
        >
          <div className="section-badge mb-3 sm:mb-4 text-xs">
            <span>Client Success Stories</span>
          </div>
          <h2 id="results-heading" className="heading-section text-foreground mb-2 sm:mb-3 text-balance">
            Trusted by Tradies & Construction Businesses{" "}
            <span className="text-gradient">Across Australia</span>
          </h2>
          <p className="text-[0.9375rem] sm:text-base text-muted-foreground leading-[1.7]">
            Real results from real Australian trade business owners who switched to our digital growth system
          </p>
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 sm:mb-8"
        >
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className="flex-none w-[85%] sm:w-[45%] lg:w-[32%]"
                >
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Bottom Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <p className="text-base sm:text-lg lg:text-xl text-foreground">
            Join trade and construction businesses across Australia getting{" "}
            <span className="font-semibold text-primary">real, measurable results</span> from their digital marketing.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
